import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { matchesService, predictionsService, playersService } from '../../services/supabaseService';
import Header from '../../components/navigation/Header';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import Icon from '../../components/AppIcon';
import MatchHeader from './components/MatchHeader';
import PredictionSection from './components/PredictionSection';
import WinnerPrediction from './components/WinnerPrediction';
import PlayerSelection from './components/PlayerSelection';
import ConfidenceSlider from './components/ConfidenceSlider';
import PredictionSummary from './components/PredictionSummary';
import Toast from '../../components/ui/Toast';
import Button from '../../components/ui/Button';

// UUID validation helper
const isValidUUID = (uuid) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex?.test(uuid);
};

const MatchPredictions = () => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const location = useLocation();
  const tournamentIdFromState = location?.state?.tournamentId;

  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(null);
  const [autoLockHours, setAutoLockHours] = useState(1);
  const [isTossLocked, setIsTossLocked] = useState(false);
  const [tossLockTime, setTossLockTime] = useState(null);
  const [openSections, setOpenSections] = useState({
    winner: true,
    toss: false,
    batting: false,
    motm: false,
    scorer: false,
    wicket: false
  });

  const [predictions, setPredictions] = useState({
    winner: null,
    winnerConfidence: 2,
    tossWinner: null,
    battingFirst: null,
    motm: null,
    motmConfidence: 2,
    highestScorer: null,
    scorerConfidence: 2,
    highestWicketTaker: null,
    wicketConfidence: 2
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tournamentId, setTournamentId] = useState(tournamentIdFromState || null);
  const [enableConfidenceSlider, setEnableConfidenceSlider] = useState(true);
  const [predictionSettings, setPredictionSettings] = useState(null);
  const [predictionsEnabled, setPredictionsEnabled] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [predictionsOpenDate, setPredictionsOpenDate] = useState(null);
  const [timeUntilOpen, setTimeUntilOpen] = useState(null);

  useEffect(() => {
    if (matchId) {
      // Validate UUID format before fetching
      if (!isValidUUID(matchId)) {
        setError('Invalid match ID format. Please select a valid match.');
        setLoading(false);
        return;
      }
      fetchMatchData();
    } else {
      setError('No match selected');
      setLoading(false);
    }
  }, [matchId]);

  // Timer effect - must be before conditional returns
  useEffect(() => {
    if (!lockTime) return;
    
    const timer = setInterval(() => {
      const now = new Date();
      const lock = new Date(lockTime);
      const diff = lock - now;
      
      setTimeRemaining(diff);
      
      if (diff <= 0) {
        setIsLocked(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [lockTime]);

  // Toss lock timer effect
  useEffect(() => {
    if (!tossLockTime) return;

    const timer = setInterval(() => {
      if (new Date() >= new Date(tossLockTime)) {
        setIsTossLocked(true);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [tossLockTime]);

  // Media query effect - must be before conditional returns
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const handleMediaChange = (e) => {
      if (e?.matches) {
        setOpenSections({
          winner: true,
          toss: true,
          batting: true,
          motm: true,
          scorer: true,
          wicket: true
        });
      } else {
        setOpenSections({
          winner: true,
          toss: false,
          batting: false,
          motm: false,
          scorer: false,
          wicket: false
        });
      }
    };

    handleMediaChange(mediaQuery);
    mediaQuery?.addEventListener('change', handleMediaChange);

    return () => mediaQuery?.removeEventListener('change', handleMediaChange);
  }, []);

  // Countdown display timer — shows time remaining until predictions open (display only)
  useEffect(() => {
    if (!predictionsOpenDate || predictionsEnabled) return;

    const updateCountdown = () => {
      const diff = new Date(predictionsOpenDate) - new Date();
      setTimeUntilOpen(diff > 0 ? diff : null);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [predictionsOpenDate, predictionsEnabled]);

  // Backend-driven polling: refresh predictions_enabled status from DB every 60 seconds
  // The backend pg_cron job enables predictions automatically; we just poll for the update
  useEffect(() => {
    if (predictionsEnabled || !matchId) return;

    const refreshStatus = async () => {
      try {
        const lockStatus = await predictionsService?.checkMatchLockStatus(matchId);
        if (lockStatus?.predictionsEnabled) {
          setPredictionsEnabled(true);
          setIsLocked(lockStatus?.isLocked || false);
          setLockTime(lockStatus?.lockTime);
          if (lockStatus?.autoLockHours !== undefined) setAutoLockHours(lockStatus?.autoLockHours);
        }
      } catch (err) {
        console.error('Error refreshing match predictions status:', err);
      }
    };

    const pollInterval = setInterval(refreshStatus, 60000); // poll every 60 seconds
    return () => clearInterval(pollInterval);
  }, [predictionsEnabled, matchId]);

  const fetchMatchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [matchData, existingPrediction, lockStatus] = await Promise.all([
        matchesService?.getById(matchId),
        predictionsService?.getMatchPrediction(matchId),
        predictionsService?.checkMatchLockStatus(matchId)
      ]);

      if (!matchData) {
        setError('Match not found');
        setLoading(false);
        return;
      }

      // Get players for the tournament
      const playersData = await playersService?.getByTournament(matchData?.tournament?.id);

      // Store tournament ID for navigation
      setTournamentId(matchData?.tournament?.id);

      // Set confidence slider setting from tournament
      setEnableConfidenceSlider(matchData?.tournament?.enableConfidenceSlider ?? true);

      // Store prediction settings from tournament
      setPredictionSettings(matchData?.tournament?.predictionSettings || null);

      // Use predictions_open_time from DB (set by backend trigger) for countdown display
      if (lockStatus?.predictionsOpenTime) {
        setPredictionsOpenDate(lockStatus?.predictionsOpenTime);
      } else {
        // Fallback: compute from match date and autoEnableDays if DB value not yet set
        const autoEnableDays = matchData?.tournament?.predictionSettings?.matchLevel?.autoEnableDays;
        if (matchData?.matchDate && autoEnableDays != null && autoEnableDays > 0) {
          const openDate = new Date(matchData?.matchDate);
          openDate?.setDate(openDate?.getDate() - autoEnableDays);
          setPredictionsOpenDate(openDate);
        }
      }

      // Set lock status
      setIsLocked(lockStatus?.isLocked || false);
      setLockTime(lockStatus?.lockTime);
      if (lockStatus?.autoLockHours !== undefined) setAutoLockHours(lockStatus?.autoLockHours);
      setPredictionsEnabled(lockStatus?.predictionsEnabled ?? true);

      // Toss lock: earlier of configured lock time or 30 min before match start
      const matchStart = new Date(matchData?.matchDate);
      const thirtyMinBefore = new Date(matchStart.getTime() - 30 * 60 * 1000);
      const configuredLock = lockStatus?.lockTime ? new Date(lockStatus.lockTime) : null;
      const computedTossLock = configuredLock
        ? new Date(Math.min(configuredLock.getTime(), thirtyMinBefore.getTime()))
        : thirtyMinBefore;
      setTossLockTime(computedTossLock);
      setIsTossLocked(new Date() >= computedTossLock);

      // Transform match data
      const transformedMatch = {
        id: matchData?.id,
        tournament: matchData?.tournament?.name,
        matchType: matchData?.matchType,
        stage: matchData?.stage,
        team1: {
          id: matchData?.team1?.id,
          name: matchData?.team1?.name,
          shortName: matchData?.team1?.shortName || matchData?.team1?.name?.substring(0, 3)?.toUpperCase(),
          flag: matchData?.team1?.logo,
          flagAlt: matchData?.team1?.logoAlt
        },
        team2: {
          id: matchData?.team2?.id,
          name: matchData?.team2?.name,
          shortName: matchData?.team2?.shortName || matchData?.team2?.name?.substring(0, 3)?.toUpperCase(),
          flag: matchData?.team2?.logo,
          flagAlt: matchData?.team2?.logoAlt
        },
        date: new Date(matchData?.matchDate)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        time: new Date(matchData?.matchDate)?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        venue: matchData?.venue,
        lockTime: lockStatus?.lockTime
      };

      // Transform players - filter to only show players from the two teams in this match
      const transformedPlayers = playersData
        ?.filter(player => 
          player?.teamId === matchData?.team1?.id || 
          player?.teamId === matchData?.team2?.id
        )
        ?.map(player => ({
          id: player?.id,
          name: player?.name,
          team: player?.teamShortName || player?.teamName?.substring(0, 3)?.toUpperCase(),
          role: player?.role
        })) || [];

      setMatch(transformedMatch);
      setPlayers(transformedPlayers);

      // Load existing predictions if any
      if (existingPrediction) {
        setPredictions({
          winner: existingPrediction?.winnerId,
          winnerConfidence: existingPrediction?.winnerConfidence || 2,
          tossWinner: existingPrediction?.tossWinnerId,
          battingFirst: existingPrediction?.battingFirstId,
          motm: existingPrediction?.manOfMatchId,
          motmConfidence: existingPrediction?.motmConfidence || 2,
          highestScorer: existingPrediction?.highestScorerId,
          scorerConfidence: existingPrediction?.scorerConfidence || 2,
          highestWicketTaker: existingPrediction?.highestWicketTakerId,
          wicketConfidence: existingPrediction?.wicketConfidence || 2
        });
      }

      if (lockStatus?.lockTime) {
        setTimeRemaining(new Date(lockStatus?.lockTime) - new Date());
      }
    } catch (err) {
      console.error('Error fetching match data:', err);
      setError('Failed to load match data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrediction = async () => {
    if (isLocked) {
      setErrorModal({ title: 'Predictions Locked', message: 'Predictions are locked. The deadline has passed.' });
      return;
    }
    if (!predictionsEnabled) {
      setErrorModal({ title: 'Predictions Not Open', message: 'Predictions are not yet open for this match.' });
      return;
    }

    try {
      setIsSaving(true);
      await predictionsService?.saveMatchPrediction(matchId, {
        winnerId: predictions?.winner,
        winnerConfidence: predictions?.winnerConfidence,
        tossWinnerId: predictions?.tossWinner,
        battingFirstId: predictions?.battingFirst,
        manOfMatchId: predictions?.motm,
        motmConfidence: predictions?.motmConfidence,
        highestScorerId: predictions?.highestScorer,
        scorerConfidence: predictions?.scorerConfidence,
        highestWicketTakerId: predictions?.highestWicketTaker,
        wicketConfidence: predictions?.wicketConfidence
      });
      setToast({ message: 'Prediction saved successfully!', type: 'success' });
    } catch (err) {
      console.error('Error saving prediction:', err);
      setErrorModal({ title: 'Save Failed', message: err?.message || 'Failed to save prediction. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading match...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Match not found'}</p>
            <button
              onClick={() => navigate('/user-dashboard')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const mockMatch = match;
  const mockPlayers = players;

  const toggleSection = (section) => {
    if (window.innerWidth < 1024) {
      setOpenSections((prev) => ({
        ...prev,
        [section]: !prev?.[section]
      }));
    }
  };

  const handlePredictionChange = (field, value) => {
    setPredictions((prev) => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error for the changed field
    const fieldToErrorMap = {
      winner: 'winner',
      tossWinner: 'tossWinner',
      battingFirst: 'battingFirst',
      motm: 'motm',
      highestScorer: 'highestScorer',
      highestWicketTaker: 'highestWicketTaker'
    };
    const errorKey = fieldToErrorMap?.[field];
    if (errorKey && value) {
      setValidationErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const calculatePoints = () => {
    let total = 0;
    const confidenceLevels = [1.0, 1.5, 2.0, 2.5];

    // Get base points from prediction settings or use defaults
    const winnerPoints = predictionSettings?.matchLevel?.matchWinner?.points || 50;
    const motmPoints = predictionSettings?.matchLevel?.manOfMatch?.points || 30;
    const scorerPoints = predictionSettings?.matchLevel?.highestRunScorer?.points || 25;
    const wicketPoints = predictionSettings?.matchLevel?.highestWicketTaker?.points || 25;
    const tossPoints = predictionSettings?.matchLevel?.tossWinner?.points || 10;
    const battingPoints = predictionSettings?.matchLevel?.battingFirst?.points || 10;

    // Use multiplier 1.0 when confidence slider is disabled
    const getMultiplier = (confidenceLevel) => {
      if (!enableConfidenceSlider) return 1.0;
      return confidenceLevels?.[confidenceLevel - 1] || 1.0;
    };

    if (predictions?.winner) total += Math.round(winnerPoints * getMultiplier(predictions?.winnerConfidence));
    if (predictions?.tossWinner) total += tossPoints;
    if (predictions?.battingFirst) total += battingPoints;
    if (predictions?.motm) total += Math.round(motmPoints * getMultiplier(predictions?.motmConfidence));
    if (predictions?.highestScorer) total += Math.round(scorerPoints * getMultiplier(predictions?.scorerConfidence));
    if (predictions?.highestWicketTaker) total += Math.round(wicketPoints * getMultiplier(predictions?.wicketConfidence));

    return total;
  };

  const calculateEarlyBonus = () => {
    // Check if early prediction bonus is enabled in tournament settings
    if (!predictionSettings?.bonusPoints?.earlyPrediction?.enabled) {
      return 0;
    }

    const hoursRemaining = timeRemaining / (1000 * 60 * 60);
    if (hoursRemaining > 24) return 20;
    if (hoursRemaining > 12) return 10;
    if (hoursRemaining > 6) return 5;
    return 0;
  };

  const handleSubmit = async () => {
    if (isLocked) {
      setErrorModal({ title: 'Predictions Locked', message: 'Predictions are locked. The deadline has passed.' });
      return;
    }
    if (!predictionsEnabled) {
      setErrorModal({ title: 'Predictions Not Open', message: 'Predictions are not yet open for this match.' });
      return;
    }

    const errors = {};

    // Winner is always required
    if (!predictions?.winner) {
      errors.winner = 'Please select a Match Winner.';
    }

    const isGroupStage = mockMatch?.stage === 'group';

    // Toss & Batting First required for non-group stage only when not yet toss-locked
    if (!isGroupStage && !isTossLocked) {
      if (!predictions?.tossWinner) errors.tossWinner = 'Please select a Toss Winner.';
      if (!predictions?.battingFirst) errors.battingFirst = 'Please select a team to bat first.';
    }

    const groupErrors = validateGroupStageFields(isGroupStage);
    if (Object.keys(groupErrors)?.length > 0) {
      Object.keys(groupErrors)?.forEach(key => {
        errors[key] = groupErrors?.[key];
      });
    }

    // MOTM always required
    if (!predictions?.motm) {
      errors.motm = 'Please select a Man of the Match.';
    }

    // Scorer & Wicket required for non-group stage
    if (!isGroupStage) {
      if (!predictions?.highestScorer) {
        errors.highestScorer = 'Please select a Highest Run Scorer.';
      }
      if (!predictions?.highestWicketTaker) {
        errors.highestWicketTaker = 'Please select a Highest Wicket Taker.';
      }
    }

    if (Object.keys(errors)?.length > 0) {
      setValidationErrors(errors);
      // Open sections with errors on mobile
      const sectionMap = {
        winner: 'winner',
        tossWinner: 'toss',
        battingFirst: 'batting',
        motm: 'motm',
        highestScorer: 'scorer',
        highestWicketTaker: 'wicket'
      };
      const sectionsToOpen = {};
      Object.keys(errors)?.forEach(key => {
        const section = sectionMap?.[key];
        if (section) sectionsToOpen[section] = true;
      });
      setOpenSections(prev => ({ ...prev, ...sectionsToOpen }));
      // Scroll to first error section
      const firstErrorKey = Object.keys(errors)?.[0];
      const firstSection = sectionMap?.[firstErrorKey];
      if (firstSection) {
        setTimeout(() => {
          const el = document.getElementById(`section-${firstSection}`);
          if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
      return;
    }

    setValidationErrors({});
    try {
      setIsSubmitting(true);
      await predictionsService?.saveMatchPrediction(matchId, {
        winnerId: predictions?.winner,
        winnerConfidence: predictions?.winnerConfidence,
        tossWinnerId: predictions?.tossWinner,
        battingFirstId: predictions?.battingFirst,
        manOfMatchId: predictions?.motm,
        motmConfidence: predictions?.motmConfidence,
        highestScorerId: predictions?.highestScorer,
        scorerConfidence: predictions?.scorerConfidence,
        highestWicketTakerId: predictions?.highestWicketTaker,
        wicketConfidence: predictions?.wicketConfidence
      });
      setToast({ message: `Predictions submitted successfully! Total Points: ${calculatePoints() + calculateEarlyBonus()}`, type: 'success' });
      setTimeout(() => {
        if (tournamentId) {
          navigate(`/tournament-details/${tournamentId}`);
        } else {
          navigate('/user-dashboard');
        }
      }, 1500);
    } catch (err) {
      console.error('Error submitting prediction:', err);
      setErrorModal({ title: 'Submission Failed', message: err?.message || 'Failed to submit prediction. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all predictions?')) {
      setPredictions({
        winner: null,
        winnerConfidence: 2,
        tossWinner: null,
        battingFirst: null,
        motm: null,
        motmConfidence: 2,
        highestScorer: null,
        scorerConfidence: 2,
        highestWicketTaker: null,
        wicketConfidence: 2
      });
    }
  };

  const teams = [mockMatch?.team1, mockMatch?.team2];
  const isGroupStage = mockMatch?.stage === 'group';

  const validateGroupStageFields = (isGroupStageParam = isGroupStage) => {
    const groupErrors = {};

    // MOTM always required
    if (!predictions?.motm) {
      groupErrors.motm = 'Please select a Man of the Match.';
    }

    // Scorer & Wicket required for non-group stage
    if (!isGroupStageParam) {
      if (!predictions?.highestScorer) {
        groupErrors.highestScorer = 'Please select a Highest Run Scorer.';
      }
      if (!predictions?.highestWicketTaker) {
        groupErrors.highestWicketTaker = 'Please select a Highest Wicket Taker.';
      }
    }
    return groupErrors;
  };

  // Get base points from prediction settings or use defaults
  const winnerPoints = predictionSettings?.matchLevel?.matchWinner?.points || 50;
  const motmPoints = predictionSettings?.matchLevel?.manOfMatch?.points || 30;
  const scorerPoints = predictionSettings?.matchLevel?.highestRunScorer?.points || 25;
  const wicketPoints = predictionSettings?.matchLevel?.highestWicketTaker?.points || 25;
  const tossPoints = predictionSettings?.matchLevel?.tossWinner?.points || 10;
  const battingPoints = predictionSettings?.matchLevel?.battingFirst?.points || 10;

  // Confidence multiplier: use 1.0 when slider is disabled, otherwise use selected confidence level
  const getConfidenceMultiplier = (confidenceLevel) => {
    if (!enableConfidenceSlider) return 1.0;
    return [1.0, 1.5, 2.0, 2.5]?.[confidenceLevel - 1] || 1.0;
  };

  const summaryData = {
    winner: predictions?.winner ? teams?.find((t) => t?.id === predictions?.winner) : null,
    winnerPoints: predictions?.winner ? Math.round(winnerPoints * getConfidenceMultiplier(predictions?.winnerConfidence)) : 0,
    tossWinner: predictions?.tossWinner ? teams?.find((t) => t?.id === predictions?.tossWinner) : null,
    tossPoints: predictions?.tossWinner ? tossPoints : 0,
    battingFirst: predictions?.battingFirst ? teams?.find((t) => t?.id === predictions?.battingFirst) : null,
    battingPoints: predictions?.battingFirst ? battingPoints : 0,
    motm: predictions?.motm ? mockPlayers?.find((p) => p?.id === predictions?.motm) : null,
    motmPoints: predictions?.motm ? Math.round(motmPoints * getConfidenceMultiplier(predictions?.motmConfidence)) : 0,
    highestScorer: predictions?.highestScorer ? mockPlayers?.find((p) => p?.id === predictions?.highestScorer) : null,
    scorerPoints: predictions?.highestScorer ? Math.round(scorerPoints * getConfidenceMultiplier(predictions?.scorerConfidence)) : 0,
    highestWicketTaker: predictions?.highestWicketTaker ? mockPlayers?.find((p) => p?.id === predictions?.highestWicketTaker) : null,
    wicketPoints: predictions?.highestWicketTaker ? Math.round(wicketPoints * getConfidenceMultiplier(predictions?.wicketConfidence)) : 0
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-elevation-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertCircle" size={24} className="text-destructive" />
              </div>
              <h3 className="text-xl font-bold text-foreground">{errorModal?.title || 'Error'}</h3>
            </div>
            <p className="text-sm md:text-base text-muted-foreground mb-6">
              {errorModal?.message || 'An error occurred. Please try again.'}
            </p>
            <Button
              variant="default"
              fullWidth
              onClick={() => setErrorModal(null)}
            >
              Close
            </Button>
          </div>
        </div>
      )}
      <Breadcrumb tournamentName={mockMatch?.tournament} tournamentId={tournamentId} currentSection="Match Predictions" />
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
          <MatchHeader match={mockMatch} timeRemaining={timeRemaining} autoLockHours={autoLockHours} />

          {isLocked && (
          <div className="bg-error/10 border border-error rounded-lg p-4 md:p-6 mb-4 md:mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Lock" size={20} className="text-error mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-error mb-2">Predictions Locked</h4>
                  <p className="text-sm md:text-base text-muted-foreground">
                    The prediction window has closed. Predictions lock 1 hour before match start to ensure fair play.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isTossLocked && !isLocked && (
            <div className="bg-warning/10 border border-warning rounded-lg p-4 md:p-6 mb-4 md:mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Lock" size={20} className="text-warning mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-warning mb-2">Toss Predictions Locked</h4>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Toss Winner and Batting First predictions are now locked. You can still update your other predictions.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!predictionsEnabled && !isLocked && (
            <div className="bg-warning/10 border border-warning rounded-lg p-4 md:p-6 mb-4 md:mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Clock" size={20} className="text-warning mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-base md:text-lg font-semibold text-warning mb-2">Predictions Not Yet Open</h4>
                  {timeUntilOpen != null && timeUntilOpen > 0 ? (
                    <>
                      {/* Days message */}
                      {Math.floor(timeUntilOpen / (1000 * 60 * 60 * 24)) > 0 && (
                        <p className="text-sm md:text-base font-medium text-foreground mb-3">
                          Predictions Open In{' '}
                          <span className="text-warning font-bold">
                            {Math.floor(timeUntilOpen / (1000 * 60 * 60 * 24))} Day{Math.floor(timeUntilOpen / (1000 * 60 * 60 * 24)) !== 1 ? 's' : ''}
                          </span>
                        </p>
                      )}
                      {/* Countdown timer */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {[
                          { label: 'Days', value: Math.floor(timeUntilOpen / (1000 * 60 * 60 * 24)) },
                          { label: 'Hours', value: Math.floor((timeUntilOpen % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)) },
                          { label: 'Mins', value: Math.floor((timeUntilOpen % (1000 * 60 * 60)) / (1000 * 60)) },
                          { label: 'Secs', value: Math.floor((timeUntilOpen % (1000 * 60)) / 1000) }
                        ]?.map((unit, idx, arr) => (
                          <React.Fragment key={unit?.label}>
                            <div className="flex flex-col items-center bg-warning/20 rounded-lg px-3 py-2 min-w-[52px]">
                              <span className="text-xl md:text-2xl font-bold text-warning leading-none">
                                {String(unit?.value)?.padStart(2, '0')}
                              </span>
                              <span className="text-xs text-muted-foreground mt-1">{unit?.label}</span>
                            </div>
                            {idx < arr?.length - 1 && (
                              <span className="text-warning font-bold text-xl">:</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm md:text-base text-muted-foreground">
                      Match predictions are not yet available. They will open automatically based on the tournament's prediction timing settings.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-4">
              <PredictionSection
                title="Match Winner"
                icon="Trophy"
                points={winnerPoints}
                isOpen={openSections?.winner}
                onToggle={() => toggleSection('winner')}
                required
                hasError={!!validationErrors?.winner}
                sectionId="section-winner"
              >

                <WinnerPrediction
                  teams={teams}
                  selectedWinner={predictions?.winner}
                  onSelect={(id) => handlePredictionChange('winner', id)}
                  disabled={isLocked}
                  error={validationErrors?.winner} />

                {enableConfidenceSlider && (
                  <div className="mt-4 md:mt-6">
                    <ConfidenceSlider
                      value={predictions?.winnerConfidence}
                      onChange={(value) => handlePredictionChange('winnerConfidence', value)}
                      disabled={isLocked}
                      basePoints={winnerPoints} />
                  </div>
                )}
              </PredictionSection>

              {!isGroupStage && (
              <PredictionSection
                title="Toss Winner"
                icon="Coins"
                points={tossPoints}
                isOpen={openSections?.toss}
                onToggle={() => toggleSection('toss')}
                required
                hasError={!!validationErrors?.tossWinner}
                sectionId="section-toss"
              >

                <WinnerPrediction
                  teams={teams}
                  selectedWinner={predictions?.tossWinner}
                  onSelect={(id) => handlePredictionChange('tossWinner', id)}
                  disabled={isLocked || isTossLocked}
                  error={validationErrors?.tossWinner} />

              </PredictionSection>
              )}

              {!isGroupStage && (
              <PredictionSection
                title="Batting First"
                icon="Target"
                points={battingPoints}
                isOpen={openSections?.batting}
                onToggle={() => toggleSection('batting')}
                required
                hasError={!!validationErrors?.battingFirst}
                sectionId="section-batting"
              >

                <WinnerPrediction
                  teams={teams}
                  selectedWinner={predictions?.battingFirst}
                  onSelect={(id) => handlePredictionChange('battingFirst', id)}
                  disabled={isLocked || isTossLocked}
                  error={validationErrors?.battingFirst} />

              </PredictionSection>
              )}

              <PredictionSection
                title="Man of the Match"
                icon="Award"
                points={motmPoints}
                isOpen={openSections?.motm}
                onToggle={() => toggleSection('motm')}
                required
                hasError={!!validationErrors?.motm}
                sectionId="section-motm"
              >

                <PlayerSelection
                  label="Select Player"
                  description="Choose the player you think will be awarded Man of the Match"
                  players={mockPlayers}
                  selectedPlayer={predictions?.motm}
                  onSelect={(value) => handlePredictionChange('motm', value)}
                  disabled={isLocked}
                  error={validationErrors?.motm}
                  required />

                {enableConfidenceSlider && (
                  <div className="mt-4 md:mt-6">
                    <ConfidenceSlider
                      value={predictions?.motmConfidence}
                      onChange={(value) => handlePredictionChange('motmConfidence', value)}
                      disabled={isLocked}
                      basePoints={motmPoints} />
                  </div>
                )}
              </PredictionSection>

              {!isGroupStage && (
              <PredictionSection
                title="Highest Run Scorer"
                icon="TrendingUp"
                points={scorerPoints}
                isOpen={openSections?.scorer}
                onToggle={() => toggleSection('scorer')}
                required
                hasError={!!validationErrors?.highestScorer}
                sectionId="section-scorer"
              >

                <PlayerSelection
                  label="Select Player"
                  description="Choose the player who will score the most runs in this match"
                  players={mockPlayers}
                  selectedPlayer={predictions?.highestScorer}
                  onSelect={(value) => handlePredictionChange('highestScorer', value)}
                  disabled={isLocked}
                  error={validationErrors?.highestScorer}
                  required />

                {enableConfidenceSlider && (
                  <div className="mt-4 md:mt-6">
                    <ConfidenceSlider
                      value={predictions?.scorerConfidence}
                      onChange={(value) => handlePredictionChange('scorerConfidence', value)}
                      disabled={isLocked}
                      basePoints={scorerPoints} />
                  </div>
                )}
              </PredictionSection>
              )}

              {!isGroupStage && (
              <PredictionSection
                title="Highest Wicket Taker"
                icon="Zap"
                points={wicketPoints}
                isOpen={openSections?.wicket}
                onToggle={() => toggleSection('wicket')}
                required
                hasError={!!validationErrors?.highestWicketTaker}
                sectionId="section-wicket"
              >

                <PlayerSelection
                  label="Select Player"
                  description="Choose the player who will take the most wickets in this match"
                  players={mockPlayers}
                  selectedPlayer={predictions?.highestWicketTaker}
                  onSelect={(value) => handlePredictionChange('highestWicketTaker', value)}
                  disabled={isLocked}
                  error={validationErrors?.highestWicketTaker}
                  required />

                {enableConfidenceSlider && (
                  <div className="mt-4 md:mt-6">
                    <ConfidenceSlider
                      value={predictions?.wicketConfidence}
                      onChange={(value) => handlePredictionChange('wicketConfidence', value)}
                      disabled={isLocked}
                      basePoints={wicketPoints} />
                  </div>
                )}
              </PredictionSection>
              )}
            </div>

            <div className="lg:col-span-1">
              <PredictionSummary
                predictions={summaryData}
                totalPoints={calculatePoints()}
                earlyBonus={calculateEarlyBonus()}
                onSubmit={handleSubmit}
                onClear={handleClear}
                isSubmitting={isSubmitting}
                disabled={isLocked || !predictionsEnabled}
                isGroupStage={isGroupStage}
                winnerError={validationErrors?.winner} />

            </div>
          </div>
        </div>
      </main>
    </div>
  );

};

export default MatchPredictions;